/**
 * Accounting > QBD Scans. The scanned bills, receipts and statements that were attached to
 * transactions in QuickBooks Desktop did not come over to QuickBooks Online. Each one has
 * been read by Claude and matched to a QuickBooks Online transaction; this is where the
 * uncertain ones get checked.
 *
 * Limited to Greg and Mercedes on the server (qbd-scans.js); the sidebar hides it from
 * everybody else as a courtesy. Nothing on this page changes QuickBooks: decisions are kept
 * in CPx until the attach step is approved.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, FileText, Check, X, Search, RotateCcw } from 'lucide-react';
import { qbdScans } from '../services/api';

const COMPANIES = [
  { realm: '9341457826908498', name: 'Crabplace.com' },
  { realm: '9341457827050807', name: 'Crab & Cruise' },
];
const VIEWS = [
  { id: 'look', label: 'Needs a Look' },
  { id: 'clean', label: 'Clean Matches' },
  { id: 'none', label: 'No Match' },
  { id: 'reviewed', label: 'Done by You' },
];
const TYPE = {
  Bill: 'Bill', BillPayment: 'Bill payment', Purchase: 'Expense or check', Deposit: 'Deposit', Invoice: 'Invoice',
  Payment: 'Customer payment', SalesReceipt: 'Sales receipt', JournalEntry: 'Journal entry', Transfer: 'Transfer',
  CreditMemo: 'Credit memo', VendorCredit: 'Vendor credit',
};
const DOC = {
  bill: 'Bill', invoice_we_sent: 'Invoice we sent', receipt: 'Receipt', statement: 'Statement', check: 'Check', deposit: 'Deposit slip',
  bank_statement: 'Bank statement', tax_form: 'Tax form', contract: 'Contract', letter: 'Letter', other: 'Other',
};
const PAGE = 10;

const money = (v) => (v == null || !isFinite(Number(v)) ? '' : (Number(v) < 0 ? '-$' : '$') + Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const day = (d) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
  if (!y || !m || !dd) return '';
  return new Date(y, m - 1, dd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const num = (n) => Number(n || 0).toLocaleString('en-US');

const box = { border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, background: 'var(--white, #fff)' };
const small = { fontSize: 12, color: 'var(--gray-500, #6b7280)' };
const cell = { padding: '6px 8px', fontSize: 13, textAlign: 'left', verticalAlign: 'top' };
const head = { ...cell, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)' };

function Stat({ label, value, tone }) {
  return (
    <div style={{ ...box, padding: '10px 14px', minWidth: 120 }}>
      <div style={small}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: tone || 'var(--navy, #003b5c)' }}>{value}</div>
    </div>
  );
}

function TxnRow({ c, onUse, useLabel, busy }) {
  return (
    <tr>
      <td style={cell}>{TYPE[c.type] || c.type}</td>
      <td style={cell}>{day(c.date)}</td>
      <td style={cell}>{c.who}</td>
      <td style={cell}>{c.num}</td>
      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>{money(c.amt)}</td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        {c.amtHit !== undefined && (
          <span style={small}>
            {[c.amtHit && 'amount', c.numHit && 'number', c.nameHit >= 0.5 && 'name'].filter(Boolean).join(', ') || 'little'} matches
          </span>
        )}
      </td>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        <a href={c.link} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>
          Open in QuickBooks <ExternalLink size={12} />
        </a>
        {onUse && (
          <button type="button" className="btn btn-primary" style={{ padding: '3px 10px', fontSize: 12, marginLeft: 6 }} disabled={busy} onClick={() => onUse(c)}>
            <Check size={12} /> {useLabel}
          </button>
        )}
      </td>
    </tr>
  );
}

function TxnTable({ rows, onUse, useLabel, busy }) {
  if (!rows || !rows.length) return null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={head}>Type</th><th style={head}>Date</th><th style={head}>Name</th><th style={head}>Number</th><th style={{ ...head, textAlign: 'right' }}>Amount</th><th style={head}>What fits</th><th style={head} /></tr></thead>
        <tbody>{rows.map((c) => <TxnRow key={c.type + c.id} c={c} onUse={onUse} useLabel={useLabel} busy={busy} />)}</tbody>
      </table>
    </div>
  );
}

function Folder({ item, realm, view, onDone }) {
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [warn, setWarn] = useState('');
  const [finding, setFinding] = useState(false);
  const first = item.files.find((f) => f.read_status === 'done') || item.files[0] || {};
  const [q, setQ] = useState({ amount: first.total != null ? String(first.total) : '', vendor: '', dateFrom: '', dateTo: '' });
  const [found, setFound] = useState(null);
  const [note, setNote] = useState('');

  const open = async (f) => {
    setErr('');
    if (viewer && viewer.id === f.id) { setViewer(null); return; }
    try { const r = await qbdScans('file_url', { fileId: f.id }); setViewer({ id: f.id, url: r.url, type: r.type, name: f.file_name }); }
    catch (e) { setErr(e.message || 'Could not open that scan.'); }
  };
  const decide = async (decision, txn) => {
    setBusy(true); setErr(''); setWarn('');
    try {
      const r = await qbdScans('decide', { folderId: item.id, decision, txnType: txn && txn.type, txnId: txn && txn.id, note: note || undefined });
      if (r.warning) setWarn(r.warning);
      onDone(item.id, !!r.warning);
    } catch (e) { setErr(e.message || 'Could not save that.'); }
    setBusy(false);
  };
  const find = async (e) => {
    e.preventDefault();
    setErr(''); setFound(null);
    try { const r = await qbdScans('search', { realm, ...q }); setFound(r.items || []); }
    catch (x) { setErr(x.message || 'Search failed.'); }
  };

  const cands = [item.best, ...(item.candidates || [])].filter(Boolean).filter((c, i, a) => a.findIndex((x) => x.type === c.type && x.id === c.id) === i);
  return (
    <div style={{ ...box, padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong>Desktop transaction {item.folder}</strong>
          <span style={{ ...small, marginLeft: 8 }}>attached {day(item.entered_on)} · {item.file_count} file{item.file_count === 1 ? '' : 's'}</span>
        </div>
        {item.match_why && view !== 'reviewed' && <div style={{ ...small, color: 'var(--orange, #dc582a)' }}>{item.match_why}</div>}
      </div>

      <div style={{ marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={head}>Scan</th><th style={head}>What it is</th><th style={head}>From</th><th style={head}>Date</th><th style={head}>Number</th><th style={{ ...head, textAlign: 'right' }}>Total</th></tr></thead>
          <tbody>
            {item.files.map((f) => (
              <tr key={f.id}>
                <td style={cell}>
                  {f.view_type !== 'none'
                    ? <button type="button" className="btn btn-outline" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => open(f)}><FileText size={12} /> {viewer && viewer.id === f.id ? 'Hide' : 'View'} {f.file_name}</button>
                    : <span style={small}>{f.file_name} (cannot be shown here)</span>}
                </td>
                <td style={cell}>
                  {f.read_status === 'done' ? (DOC[f.doc_type] || f.doc_type || '') : <span style={small}>{f.read_status === 'error' ? 'Could not be read' : f.read_status === 'skipped' ? 'Not read' : 'Not read yet'}</span>}
                  {f.description && <div style={small}>{f.description}</div>}
                </td>
                <td style={cell}>{f.vendor}{f.bill_to && <div style={small}>to {f.bill_to}</div>}</td>
                <td style={cell}>{day(f.doc_date)}</td>
                <td style={cell}>{f.doc_number}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{money(f.total)}{(f.other_amounts || []).length > 0 && <div style={small}>also {(f.other_amounts || []).map(money).join(', ')}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewer && (
        <div style={{ marginTop: 10 }}>
          {viewer.type === 'image'
            ? <img src={viewer.url} alt={viewer.name} style={{ maxWidth: '100%', border: '1px solid var(--gray-200, #e5e7eb)' }} />
            : <iframe src={viewer.url} title={viewer.name} style={{ width: '100%', height: 720, border: '1px solid var(--gray-200, #e5e7eb)' }} />}
          <div style={small}><a href={viewer.url} target="_blank" rel="noopener noreferrer">Open the scan in a new tab</a></div>
        </div>
      )}

      {view === 'reviewed' ? (
        <div style={{ marginTop: 12 }}>
          {item.review_decision === 'not_in_qbo'
            ? <div><strong>Marked not in QuickBooks</strong></div>
            : <div><strong>Tied to this transaction:</strong><TxnTable rows={item.reviewTxn ? [item.reviewTxn] : []} /></div>}
          <div style={small}>By {item.reviewed_by} on {day(item.reviewed_at)}{item.review_note ? `. Note: ${item.review_note}` : ''}</div>
          <button type="button" className="btn btn-ghost" style={{ marginTop: 6, fontSize: 12 }} disabled={busy} onClick={() => decide('clear')}><RotateCcw size={12} /> Undo</button>
        </div>
      ) : (
        <div style={{ marginTop: 12 }}>
          {cands.length > 0 ? (
            <>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{view === 'clean' ? 'Matched to' : 'Possible matches in QuickBooks'}</div>
              <TxnTable rows={view === 'clean' ? cands.slice(0, 1) : cands} onUse={(c) => decide('confirm', c)} useLabel={view === 'clean' ? 'Right' : 'This one'} busy={busy} />
            </>
          ) : <div style={small}>No QuickBooks transaction within 45 days has this amount or number.</div>}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
            <button type="button" className="btn btn-outline" onClick={() => setFinding(!finding)}><Search size={13} /> Find a Different Transaction</button>
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => decide('not_in_qbo')}><X size={13} /> Not in QuickBooks</button>
            <input className="form-input" style={{ maxWidth: 320 }} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          {finding && (
            <form onSubmit={find} style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={small}>Amount<br /><input className="form-input" style={{ width: 120 }} value={q.amount} onChange={(e) => setQ({ ...q, amount: e.target.value })} /></label>
              <label style={small}>Name, number or memo<br /><input className="form-input" style={{ width: 220 }} value={q.vendor} onChange={(e) => setQ({ ...q, vendor: e.target.value })} /></label>
              <label style={small}>From<br /><input type="date" className="form-input" value={q.dateFrom} onChange={(e) => setQ({ ...q, dateFrom: e.target.value })} /></label>
              <label style={small}>To<br /><input type="date" className="form-input" value={q.dateTo} onChange={(e) => setQ({ ...q, dateTo: e.target.value })} /></label>
              <button type="submit" className="btn btn-navy">Search</button>
            </form>
          )}
          {found && (found.length
            ? <div style={{ marginTop: 8 }}><TxnTable rows={found} onUse={(c) => decide('pick', c)} useLabel="Use this one" busy={busy} />{found.length >= 60 && <div style={small}>Showing the 60 most recent. Narrow the search to see others.</div>}</div>
            : <div style={{ ...small, marginTop: 8 }}>Nothing in QuickBooks fits that search.</div>)}
        </div>
      )}
      {warn && <div style={{ marginTop: 8, color: 'var(--orange, #dc582a)', fontSize: 13 }}>{warn}</div>}
      {err && <div style={{ marginTop: 8, color: 'var(--red, #b91c1c)', fontSize: 13 }}>{err}</div>}
    </div>
  );
}

export default function QbdScans() {
  const [realm, setRealm] = useState(COMPANIES[0].realm);
  const [view, setView] = useState('look');
  const [sum, setSum] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const loadSum = useCallback(async () => {
    try { setSum(await qbdScans('summary')); } catch (e) { setErr(e.message || 'Could not load QBD Scans.'); }
  }, []);
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await qbdScans('list', { realm, view, offset, limit: PAGE, q: search || undefined, shuffle: view === 'clean', seed: 'spot' });
      setItems(r.items || []); setTotal(r.total || 0);
    } catch (e) { setErr(e.message || 'Could not load the list.'); }
    setLoading(false);
  }, [realm, view, offset, search]);

  useEffect(() => { loadSum(); }, [loadSum]);
  useEffect(() => { load(); }, [load]);

  const itemsNow = useRef(items); itemsNow.current = items;
  // A decided folder leaves the list on the server too, so the next ones slide up into this
  // page. When the last one on the page is decided, fetch the page again rather than leave
  // it blank (Mercedes, Oct 19, 2026: she had to press Next after every 10).
  const done = (id, keep) => {
    if (!keep) {
      const left = itemsNow.current.filter((x) => x.id !== id);
      setItems(left); setTotal((t) => Math.max(0, t - 1));
      if (!left.length) { if (offset > 0 && offset >= total - 1) setOffset(Math.max(0, offset - PAGE)); else load(); }
    }
    loadSum();
    if (view === 'reviewed') load();
  };
  const resume = async () => {
    setMsg('');
    try { await qbdScans('resume_reading', { clearPause: true }); setMsg('Reading has been restarted. Counts update as it goes.'); loadSum(); }
    catch (e) { setErr(e.message || 'Could not restart reading.'); }
  };
  const rematch = async () => {
    setMsg('');
    try { await qbdScans('rematch', { realm }); setMsg('Matching has been restarted for this company. It takes a few minutes.'); }
    catch (e) { setErr(e.message || 'Could not restart matching.'); }
  };

  const co = sum && sum.companies.find((c) => c.realm === realm);
  const counts = co ? { look: co.look, clean: co.clean, none: co.none, reviewed: co.matchedByReview + co.notInQbo } : {};

  return (
    <div className="page-content" style={{ padding: 20, maxWidth: 1300 }}>
      <h1 style={{ margin: '0 0 4px' }}>QBD Scans</h1>
      <p style={{ ...small, fontSize: 14, margin: '0 0 14px', maxWidth: 820 }}>
        The scans that were attached to transactions in QuickBooks Desktop, each matched to its transaction in QuickBooks Online.
        Check the ones that need a look. Nothing here changes QuickBooks; your answers are saved here until the files are attached.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {COMPANIES.map((c) => (
          <button key={c.realm} type="button" className={realm === c.realm ? 'btn btn-navy' : 'btn btn-outline'} onClick={() => { setRealm(c.realm); setOffset(0); }}>{c.name}</button>
        ))}
      </div>

      {co && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <Stat label="Scans" value={num(co.files)} />
          <Stat label="Read" value={num(co.read)} />
          <Stat label="Waiting to be read" value={num(co.waiting)} />
          <Stat label="Could not be read" value={num(co.errors)} />
          <Stat label="Transactions (folders)" value={num(co.folders)} />
          <Stat label="Clean" value={num(co.clean)} tone="var(--green, #15803d)" />
          <Stat label="Needs a look" value={num(co.look)} tone="var(--orange, #dc582a)" />
          <Stat label="No match" value={num(co.none)} />
          <Stat label="Done by you" value={num(counts.reviewed)} />
          {co.matchWaiting > 0 && <Stat label="Not matched yet" value={num(co.matchWaiting)} />}
        </div>
      )}
      {sum && sum.paused && (
        <div style={{ ...box, padding: 10, marginBottom: 12, borderColor: 'var(--orange, #dc582a)' }}>
          Reading is paused. {sum.paused} <button type="button" className="btn btn-outline" style={{ marginLeft: 8 }} onClick={resume}>Restart Reading</button>
        </div>
      )}
      {sum && !sum.paused && co && co.waiting > 0 && !sum.readerRunning && (
        <div style={{ ...box, padding: 10, marginBottom: 12 }}>
          {num(co.waiting)} scans are waiting and reading is not running. <button type="button" className="btn btn-outline" style={{ marginLeft: 8 }} onClick={resume}>Restart Reading</button>
        </div>
      )}
      {msg && <div style={{ marginBottom: 10, color: 'var(--green, #15803d)' }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {VIEWS.map((v) => (
          <button key={v.id} type="button" className={view === v.id ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => { setView(v.id); setOffset(0); }}>
            {v.label}{counts[v.id] != null ? ` (${num(counts[v.id])})` : ''}
          </button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); setOffset(0); setSearch(q.trim()); }} style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <input className="form-input" placeholder="Vendor, number, amount or file" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} />
          <button type="submit" className="btn btn-navy"><Search size={13} /></button>
        </form>
        <button type="button" className="btn btn-ghost" onClick={rematch} title="Run the matching again for this company">Match Again</button>
      </div>
      {view === 'clean' && <p style={{ ...small, marginTop: -4 }}>These are shown in a mixed-up order so a few checks cover all the years. Press Right if it is right, or pick a different one.</p>}

      {err && <div style={{ color: 'var(--red, #b91c1c)', marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={small}>Loading...</div> : (
        items.length === 0 ? <div style={small}>Nothing here.</div> : items.map((it) => <Folder key={it.id} item={it} realm={realm} view={view} onDone={done} />)
      )}

      {total > PAGE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn btn-outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
          <span style={small}>{num(offset + 1)} to {num(Math.min(offset + items.length, total))} of {num(total)}</span>
          <button type="button" className="btn btn-outline" disabled={offset + items.length >= total} onClick={() => setOffset(offset + items.length)}>Next</button>
        </div>
      )}
    </div>
  );
}
